import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const dateKey = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
};

export const eventDate = (ev) =>
  new Date(ev?.start_date_local || ev?.start_date || ev?.date);

export const flyerSrc = (ev) =>
  ev?.image ||
  ev?.promo?.story ||
  ev?.promo?.post ||
  ev?.promo?.banner ||
  "";

const formatShowTime = (ev) => {
  if (ev?.start_time_display) {
    return [ev.start_time_display, ev.timezone_abbr].filter(Boolean).join(" ");
  }
  const d = eventDate(ev);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const monthKey = (ev) => {
  const d = eventDate(ev);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${d.getMonth()}`;
};

const monthLabel = (ev) => {
  const d = eventDate(ev);
  if (isNaN(d)) return "";
  return MONTHS[d.getMonth()];
};

function ShowRailCard({ ev, rowRef }) {
  const flyer = flyerSrc(ev);
  const href = ev.to || `/events/event/${ev.slug}`;
  const d = eventDate(ev);
  const dayNum = isNaN(d) ? "" : d.getDate();
  const weekday = isNaN(d)
    ? ""
    : d.toLocaleDateString("en-US", { weekday: "short" });
  const time = formatShowTime(ev);

  return (
    <div ref={rowRef} className="group flex flex-col">
      <div className="flex gap-3 rounded-xl border-[0.5px] border-white/10 bg-ink-900/60 p-2.5 transition hover:border-brand-blue/40 hover:bg-ink-900/80">
        <Link
          to={href}
          className="relative h-[84px] w-[60px] shrink-0 overflow-hidden rounded-lg ring-[0.5px] ring-white/10"
        >
          {flyer ? (
            <img
              src={flyer}
              alt=""
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            />
          ) : (
            <span className="block h-full w-full bg-ink-600" />
          )}
        </Link>

        <div className="flex min-w-0 flex-1 flex-col justify-evenly py-0.5">
          <div className="flex flex-nowrap items-baseline gap-2 whitespace-nowrap">
            <span className="shrink-0 text-[18px] font-extrabold leading-none tracking-tight text-white">
              {dayNum}
            </span>
            <span className="shrink-0 text-[12px] font-semibold uppercase tracking-wide text-brand-blue-bright">
              {weekday}
            </span>
            {time ? (
              <span className="shrink-0 text-[12px] text-white/50">{time}</span>
            ) : null}
          </div>
          <Link
            to={href}
            className="truncate text-[14px] font-semibold leading-snug text-white no-underline transition group-hover:text-brand-blue-bright"
          >
            {ev.name}
          </Link>
          {(ev.location || ev.organization?.name) && (
            <p className="truncate text-[11px] leading-snug text-white/35">
              {ev.location || ev.organization?.name}
            </p>
          )}
        </div>
      </div>

      <Link
        to={href}
        className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-blue px-3 py-2 text-[12px] font-semibold text-white no-underline transition hover:bg-brand-blue-hover"
      >
        Get tickets
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
          <path
            d="M5 12h14M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Link>
    </div>
  );
}

/**
 * Interactive month calendar + scrollable upcoming-shows rail with flyers.
 */
export default function EventCalendar({
  events = [],
  selectedKey,
  onSelectDay,
  className = "",
}) {
  const [cursor, setCursor] = useState(() => new Date());
  const railRef = useRef(null);
  const rowRefs = useRef({});

  const sorted = useMemo(
    () =>
      [...(events || [])]
        .filter((e) => !isNaN(eventDate(e)))
        .sort((a, b) => eventDate(a) - eventDate(b)),
    [events]
  );

  const byDay = useMemo(() => {
    const map = {};
    sorted.forEach((ev) => {
      const k = dateKey(eventDate(ev));
      if (!k) return;
      (map[k] = map[k] || []).push(ev);
    });
    return map;
  }, [sorted]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const todayKey = dateKey(new Date());

  const railShows = useMemo(() => {
    if (selectedKey) return byDay[selectedKey] || [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return sorted.filter((ev) => eventDate(ev) >= now);
  }, [selectedKey, byDay, sorted]);

  const railByMonth = useMemo(() => {
    if (selectedKey) return null;
    const groups = [];
    let current = null;
    railShows.forEach((ev) => {
      const key = monthKey(ev);
      if (!key) return;
      if (!current || current.key !== key) {
        current = { key, label: monthLabel(ev), shows: [] };
        groups.push(current);
      }
      current.shows.push(ev);
    });
    return groups;
  }, [selectedKey, railShows]);

  const railHeading = selectedKey
    ? (() => {
        const [y, m, d] = selectedKey.split("-").map(Number);
        return new Date(y, m, d).toLocaleDateString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric",
        });
      })()
    : "Coming up next";

  const changeMonth = (delta) => {
    onSelectDay?.(null);
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  };

  const toggleDay = (k, has) => {
    if (!has) return;
    onSelectDay?.(selectedKey === k ? null : k);
  };

  // Keep rail scrolled to the first show for the selected day (desktop vertical rail)
  useEffect(() => {
    if (!selectedKey || !railShows.length) return;
    const first = railShows[0];
    const id = first._id || first.slug || first.key;
    const el = rowRefs.current[id];
    if (el && railRef.current) {
      el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
    }
  }, [selectedKey, railShows]);

  const emptyMessage = selectedKey
    ? "No shows on this date."
    : "No upcoming shows yet.";

  const clearSelectionBtn = () =>
    selectedKey ? (
      <button
        type="button"
        onClick={() => onSelectDay?.(null)}
        aria-label="Back to all shows"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border-[0.5px] border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[12px] font-semibold text-white/80 transition hover:border-brand-blue/50 hover:bg-brand-blue/10 hover:text-brand-blue-bright"
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        All shows
      </button>
    ) : null;

  const renderShowList = (opts = {}) => {
    const { horizontal = false } = opts;
    if (railShows.length === 0) {
      return (
        <p className="rounded-lg border-[0.5px] border-dashed border-white/10 px-4 py-8 text-center text-sm text-white/40">
          {emptyMessage}
        </p>
      );
    }

    // Selected day: flat list (no month grouping)
    if (selectedKey || !railByMonth?.length) {
      if (horizontal) {
        return (
          <div className="mobile-show-scroll -mr-4 flex gap-3 overflow-x-auto pr-4 pb-1 snap-x snap-mandatory sm:-mr-5 sm:pr-5">
            {railShows.map((ev) => {
              const id = ev._id || ev.slug || ev.key;
              return (
                <div
                  key={id}
                  className="w-[calc(100%-1.25rem)] shrink-0 grow-0 basis-[calc(100%-1.25rem)] snap-start"
                  ref={(node) => {
                    if (node) rowRefs.current[`m-${id}`] = node;
                  }}
                >
                  <ShowRailCard ev={ev} />
                </div>
              );
            })}
          </div>
        );
      }

      return (
        <div className="space-y-3">
          {railShows.map((ev) => {
            const id = ev._id || ev.slug || ev.key;
            return (
              <ShowRailCard
                key={id}
                ev={ev}
                rowRef={(node) => {
                  if (node) rowRefs.current[id] = node;
                }}
              />
            );
          })}
        </div>
      );
    }

    // Coming up next: one swipe row — sticky month label stays until next month replaces it
    if (horizontal) {
      return (
        <div className="mobile-show-scroll -mr-4 flex items-start gap-3 overflow-x-auto pr-4 pb-1 snap-x snap-mandatory [container-type:inline-size] sm:-mr-5 sm:pr-5">
          {railByMonth.map((group, groupIndex) => (
            <React.Fragment key={group.key}>
              {groupIndex > 0 && (
                <div
                  className="mt-8 flex shrink-0 snap-center items-stretch self-stretch px-0.5"
                  aria-hidden
                >
                  <span className="w-px bg-white/35" />
                </div>
              )}
              <div className="relative flex shrink-0 flex-col">
                <span className="sticky left-0 z-[2] mb-2 w-max whitespace-nowrap bg-ink-700/95 py-0.5 pr-4 text-lg font-extrabold leading-none tracking-tight text-white backdrop-blur-sm">
                  {group.label}
                </span>
                <div className="flex gap-3">
                  {group.shows.map((ev) => {
                    const id = ev._id || ev.slug || ev.key;
                    return (
                      <div
                        key={id}
                        className="w-[calc(100cqi-1.25rem)] shrink-0 grow-0 basis-[calc(100cqi-1.25rem)] snap-start"
                        ref={(node) => {
                          if (node) rowRefs.current[`m-${id}`] = node;
                        }}
                      >
                        <ShowRailCard ev={ev} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {railByMonth.map((group) => (
          <div key={group.key}>
            <h4 className="mb-3 sticky top-0 z-[1] bg-ink-700/95 py-1.5 text-2xl font-extrabold tracking-tight text-white backdrop-blur-sm">
              {group.label}
            </h4>
            <div className="space-y-3">
              {group.shows.map((ev) => {
                const id = ev._id || ev.slug || ev.key;
                return (
                  <ShowRailCard
                    key={id}
                    ev={ev}
                    rowRef={(node) => {
                      if (node) rowRefs.current[id] = node;
                    }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-[0.5px] border-white/10 bg-ink-700/80 ${className}`}
    >
      {/* Brand atmosphere */}
      <div
        className="pointer-events-none absolute inset-0 opacity-90"
        style={{
          background:
            "radial-gradient(70% 80% at 0% 0%, rgba(0,174,239,0.14), transparent 55%), radial-gradient(50% 60% at 100% 100%, rgba(5,123,170,0.12), transparent 50%)",
        }}
      />

      <div className="relative lg:pr-[400px]">
        {/* —— Calendar —— */}
        <div className="border-b border-white/10 p-4 sm:p-5 lg:border-b-0 lg:border-r lg:p-6">
          <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
            <div className="min-w-0">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright sm:mb-1 sm:text-[11px]">
                Show calendar
              </p>
              <h2 className="truncate text-lg font-bold tracking-tight text-white sm:text-xl lg:text-2xl">
                {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
              </h2>
            </div>
            <div className="flex shrink-0 gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                aria-label="Previous month"
                className="flex h-9 w-9 items-center justify-center rounded-md border-[0.5px] border-white/10 text-white/70 transition active:bg-brand-blue/15 hover:border-brand-blue/50 hover:bg-brand-blue/10 hover:text-white"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
                  <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                aria-label="Next month"
                className="flex h-9 w-9 items-center justify-center rounded-md border-[0.5px] border-white/10 text-white/70 transition active:bg-brand-blue/15 hover:border-brand-blue/50 hover:bg-brand-blue/10 hover:text-white"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
                  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>

          <div className="mb-1 grid grid-cols-7 gap-1 sm:mb-1.5 sm:gap-1.5">
            {WEEKDAYS.map((w, i) => (
              <div
                key={`${w}-${i}`}
                className="pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-white/40 sm:text-[11px]"
              >
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
            {grid.map((day, i) => {
              if (!day) {
                return (
                  <div
                    key={i}
                    className="aspect-square sm:h-14 sm:aspect-auto lg:h-[68px]"
                  />
                );
              }
              const k = dateKey(day);
              const dayEvents = byDay[k] || [];
              const has = dayEvents.length > 0;
              const isToday = k === todayKey;
              const isSelected = k === selectedKey;
              const thumb = has ? flyerSrc(dayEvents[0]) : "";

              return (
                <button
                  key={i}
                  type="button"
                  disabled={!has}
                  onClick={() => toggleDay(k, has)}
                  title={has ? dayEvents.map((e) => e.name).join(", ") : undefined}
                  className={`group relative aspect-square overflow-hidden rounded-md transition sm:h-14 sm:aspect-auto sm:rounded-lg lg:h-[68px] ${
                    isSelected
                      ? "ring-2 ring-brand-blue-bright ring-offset-1 ring-offset-ink-700"
                      : has
                      ? "active:ring-1 active:ring-brand-blue/60 sm:hover:ring-1 sm:hover:ring-brand-blue/60"
                      : ""
                  }`}
                >
                  {has && thumb ? (
                    <>
                      <img
                        src={thumb}
                        alt=""
                        className={`absolute inset-0 h-full w-full object-cover transition duration-300 ${
                          isSelected
                            ? "scale-110 opacity-80"
                            : "opacity-55 group-hover:opacity-70"
                        }`}
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/30 to-transparent" />
                    </>
                  ) : (
                    <span
                      className={`absolute inset-0 ${
                        has ? "bg-brand-blue/10" : "bg-white/[0.03]"
                      }`}
                    />
                  )}

                  <span
                    className={`relative z-10 flex h-full items-start justify-start p-0.5 text-[11px] font-semibold sm:p-1.5 sm:text-sm ${
                      isToday
                        ? "text-brand-blue-bright"
                        : has
                        ? "text-white"
                        : "text-white/30"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full sm:h-6 sm:w-6 ${
                        isToday ? "bg-brand-blue text-[10px] text-white sm:text-[11px]" : ""
                      }`}
                    >
                      {day.getDate()}
                    </span>
                  </span>

                  {has && (
                    <span
                      className="absolute bottom-1 left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 sm:bottom-1.5"
                      aria-hidden
                    >
                      {Array.from({
                        length: Math.min(dayEvents.length, 3),
                      }).map((_, di) => (
                        <span
                          key={di}
                          className="h-1 w-1 rounded-full bg-brand-blue-bright sm:h-1.5 sm:w-1.5"
                        />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedKey ? (
            <div className="mt-3 flex items-center justify-between gap-3 sm:mt-4">
              <p className="text-[11px] text-white/45 sm:text-[12px]">
                Showing shows for the selected date.
              </p>
              {clearSelectionBtn()}
            </div>
          ) : (
            <>
              <p className="mt-3 text-[11px] text-white/45 sm:mt-4 sm:text-[12px] lg:hidden">
                Tap a day with a flyer, then swipe the shows below.
              </p>
              <p className="mt-4 hidden text-[12px] text-white/45 lg:block">
                Tap a flyer day to preview that night&apos;s shows →
              </p>
            </>
          )}
        </div>

        {/* —— Mobile / tablet: horizontal show strip (no nested vertical scroll) —— */}
        <div className="relative p-4 sm:p-5 lg:hidden">
          <div className="mb-3 flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright sm:mb-1 sm:text-[11px]">
                {selectedKey ? "This night" : "Next up"}
              </p>
              <h3 className="truncate text-lg font-bold tracking-tight text-white sm:text-xl">
                {railHeading}
              </h3>
            </div>
            {clearSelectionBtn()}
          </div>
          {renderShowList({ horizontal: true })}
        </div>

        {/* —— Desktop: vertical rail matched to calendar height —— */}
        <div className="absolute inset-y-0 right-0 hidden w-[400px] flex-col border-l border-white/10 p-6 lg:flex">
          <div className="mb-4 flex shrink-0 items-end justify-between gap-2">
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-eyebrow text-brand-blue-bright">
                {selectedKey ? "This night" : "Next up"}
              </p>
              <h3 className="text-lg font-bold tracking-tight text-white">
                {railHeading}
              </h3>
            </div>
            {clearSelectionBtn()}
          </div>

          <div
            ref={railRef}
            className="custom-show-scroll -mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-1"
          >
            {renderShowList({ horizontal: false })}
          </div>
        </div>
      </div>

      <style>{`
        .custom-show-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(0,174,239,0.45) transparent;
        }
        .custom-show-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .custom-show-scroll::-webkit-scrollbar-thumb {
          background: rgba(0,174,239,0.4);
          border-radius: 999px;
        }
        .custom-show-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .mobile-show-scroll {
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .mobile-show-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
